import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { Button, Input } from "@lumanity/ui/primitives";
import { moveItem } from "./model";

interface KeyMessagesInputProps {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholders?: string[];
  disabled?: boolean;
}

/** Rank-ordered key messages: numbered inputs with move up/down, add and remove. */
const KeyMessagesInput = ({ id, value, onChange, placeholders = [], disabled }: KeyMessagesInputProps) => {
  const setAt = (index: number, text: string) => onChange(value.map((m, i) => (i === index ? text : m)));
  const removeAt = (index: number) => {
    const next = value.filter((_, i) => i !== index);
    onChange(next.length ? next : [""]);
  };

  return (
    <div className="space-y-2">
      <ol className="space-y-2">
        {value.map((message, i) => (
          // Index keys: the rows are positions in a ranking, and the inputs are fully controlled.
          <li key={i} className="flex items-center gap-2">
            <span className="flex w-6 h-6 shrink-0 items-center justify-center rounded-full bg-brand-light-teal text-xs font-semibold text-brand-navy">
              {i + 1}
            </span>
            <Input
              id={i === 0 ? id : undefined}
              value={message}
              placeholder={placeholders[i] ?? "Another key message"}
              onChange={(e) => setAt(i, e.target.value)}
              aria-label={`Key message ${i + 1}`}
              disabled={disabled}
            />
            <div className="flex shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onChange(moveItem(value, i, i - 1))}
                disabled={disabled || i === 0}
                aria-label={`Move key message ${i + 1} up`}
              >
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onChange(moveItem(value, i, i + 1))}
                disabled={disabled || i === value.length - 1}
                aria-label={`Move key message ${i + 1} down`}
              >
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => removeAt(i)}
                disabled={disabled || (value.length === 1 && message === "")}
                aria-label={`Remove key message ${i + 1}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </li>
        ))}
      </ol>
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, ""])} disabled={disabled}>
        <Plus className="w-4 h-4 mr-1" /> Add key message
      </Button>
    </div>
  );
};

export default KeyMessagesInput;
